import React, { createContext, useContext } from "react";

export type MedicalHistoryThemeColors = {
  /** Screen background */
  screenBg: string;
  /** Primary text color */
  text: string;
  /** Secondary/muted text */
  textMuted: string;
  /** Faint text (captions, hints) */
  textFaint: string;
  /** Primary accent color */
  accent: string;
  /** Accent color for selected state (icon tint) */
  accentLight: string;
  /** Option button background */
  optionBg: string;
  /** Option button border */
  optionBorder: string;
  /** Option button selected background */
  optionSelectedBg: string;
  /** Opaque inner surface used behind selected gradient borders */
  optionSelectedSurface: string;
  /** Option button selected border */
  optionSelectedBorder: string;
  /** Option button selected shadow color */
  optionSelectedShadow: string;
  /** Icon container background */
  iconBg: string;
  /** Icon container border */
  iconBorder: string;
  /** Icon default color */
  iconColor: string;
  /** Primary button background */
  buttonBg: string;
  /** Complete screen gradient colors */
  completeGradient: [string, string];
  /** Complete screen button inner bg */
  completeButtonBg: string;
  /** Input field background */
  inputBg: string;
  /** Input field border */
  inputBorder: string;
  /** Input placeholder text color */
  inputPlaceholder: string;
  /** Counter/dynamic field accent */
  counterAccent: string;
  /** Button border (for counter +/- buttons) */
  buttonBorder: string;
  /** NativeWind class for screen background */
  screenBgClass: string;
  /** Input appearance mode for DateInput, Select, TextField */
  inputAppearance: "light" | "dark";
  /** Card background */
  cardBg: string;
  /** Card title text color */
  cardTitle: string;
  /** Card field label text color */
  cardLabel: string;
};

// Default: Olly dark theme (unchanged)
const DARK_THEME: MedicalHistoryThemeColors = {
  screenBg: "#020617", // slate-950
  text: "#ffffff",
  textMuted: "#cbd5e1", // slate-300
  textFaint: "#94a3b8", // slate-400
  accent: "#4f46e5", // indigo
  accentLight: "#C4B5FD", // violet-300
  optionBg: "rgba(15, 23, 42, 0.5)",
  optionBorder: "rgba(255, 255, 255, 0.1)",
  optionSelectedBg: "rgba(15, 23, 42, 1)",
  optionSelectedSurface: "#0F172A",
  optionSelectedBorder: "rgba(196, 181, 253, 0.5)",
  optionSelectedShadow: "#C4B5FD",
  iconBg: "rgba(255, 255, 255, 0.05)",
  iconBorder: "rgba(255, 255, 255, 0.05)",
  iconColor: "#94a3b8",
  buttonBg: "#4f46e5",
  completeGradient: ["#4ADE80", "#C4B5FD"],
  completeButtonBg: "#020617",
  inputBg: "#020617",
  inputBorder: "rgba(255, 255, 255, 0.1)",
  inputPlaceholder: "#94a3b8",
  counterAccent: "#C4B5FD",
  buttonBorder: "rgba(255, 255, 255, 0.1)",
  screenBgClass: "bg-background",
  inputAppearance: "dark",
  cardBg: "rgba(255, 255, 255, 0.05)",
  cardTitle: "#ffffff",
  cardLabel: "#cbd5e1",
};

// Moon warm theme
export const MOON_THEME: MedicalHistoryThemeColors = {
  screenBg: "#FAF6F2",
  text: "#2A2522",
  textMuted: "#5A4D45",
  textFaint: "#8A7D74",
  accent: "#B84A16",
  accentLight: "#B84A16",
  optionBg: "rgba(255, 255, 255, 0.5)",
  optionBorder: "rgba(184, 74, 22, 0.12)",
  optionSelectedBg: "rgba(184, 74, 22, 0.08)",
  optionSelectedSurface: "#F9F9F7",
  optionSelectedBorder: "rgba(184, 74, 22, 0.4)",
  optionSelectedShadow: "#B84A16",
  iconBg: "rgba(184, 74, 22, 0.06)",
  iconBorder: "rgba(184, 74, 22, 0.1)",
  iconColor: "#8A7D74",
  buttonBg: "#B84A16",
  completeGradient: ["#B84A16", "#D4976A"],
  completeButtonBg: "#FAF6F2",
  inputBg: "rgba(255, 255, 255, 0.6)",
  inputBorder: "rgba(184, 74, 22, 0.15)",
  inputPlaceholder: "#8A7D74",
  counterAccent: "#B84A16",
  buttonBorder: "rgba(184, 74, 22, 0.2)",
  screenBgClass: "bg-[#FAF6F2]",
  inputAppearance: "light",
  cardBg: "rgba(255, 255, 255, 0.6)",
  cardTitle: "#2A2522",
  cardLabel: "#8A7D74",
};

const MedicalHistoryThemeContext =
  createContext<MedicalHistoryThemeColors>(DARK_THEME);

export function MedicalHistoryThemeProvider({
  theme,
  children,
}: {
  theme: MedicalHistoryThemeColors;
  children: React.ReactNode;
}) {
  return (
    <MedicalHistoryThemeContext.Provider value={theme}>
      {children}
    </MedicalHistoryThemeContext.Provider>
  );
}

export function useMedicalHistoryTheme(): MedicalHistoryThemeColors {
  return useContext(MedicalHistoryThemeContext);
}
